import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { MDBRow, MDBCol } from "mdbreact";
import moment from "moment/moment";
import CIIVerifiedInput from "../CIIVerifiedInput/CIIVerifiedInput";

import "../CIIData.scss";

import { Button, CheckboxInput, Input, Label } from "abs-ui-components";

import { CII_DATA } from "../CIIDataConstants";
import { ROLE_TYPES } from "../../../../../constants/roles";
import { getCIIdata, confirmManualCIIUpdate, fetchManualCII ,isVesselCapacityChanged} from "../../../../../services/CIIData-service.js";
import { emitToasterMsg } from "../../../../../services/toasterUtility.js";
import { preventScientificNotation, preventSpecialCharacters, roundOff } from "../../../../../services/utilities.js";
import { accessAPIForResponse } from "../../../../../services/api-services/api-implementations.js";

const CIIExclusionCalculation = ({ isVesselEmissionOverviewCII }) => {
	const {
		control,
		setValue,
		getValues,
		formState: { errors },
		handleSubmit,
		reset,
	} = useForm();
	const [CIIData, setCIIData] = useState([]);
	const [lastVoyageDate, setLastVoyageDate] = useState("");
	const [ciiRefreshTrigger, setCiiRefreshTrigger] = useState(0);
	const [loader, setLoader] = useState(false);
	const reportDetails = useSelector((state) => state?.reports?.selectedReportDetails);
	const user = useSelector((state) => state?.userInfo?.userData);
	const [reportFlags, setReportFlags] = useState(null);
	const [isRequiredCIIEdited, setIsRequiredCIIEdited] = useState(false);
	const [isWithoutExclusionEdited, setIsWithoutExclusionEdited] = useState(false);
    const [isWithExclusionEdited, setIsWithExclusionEdited] = useState(false);
	const [isCapacityChangeAvailable, setIsCapacityChangeAvailable] = useState(false);

	const normalizedUserRoles = (user?.roles ?? user?.Roles ?? []).map((role) => String(role || "").toUpperCase().trim());
	const canEditMajorConversion = [
		ROLE_TYPES.ER_SUPERADMIN,
		ROLE_TYPES.ER_REVIEWER,
		ROLE_TYPES.ER_VERIFIER,
	].some((role) => normalizedUserRoles.includes(role));

	const ciiExclusion = getValues("ciiExclusion");

	const getFormattedLastVoyageDate = () => {
		const date = moment(lastVoyageDate, "DD MMM YYYY");

		date.set({
			hour: 11,
			minute: 59,
			second: 59,
		});

		return date.format("MM-DD-YYYY HH:mm:ss");
	};
	
useEffect(() => {
const checkVesselCapacityChange = async () => {
const vesselMstId = reportDetails?.VesselMstId;
    if (!vesselMstId) {
        setIsCapacityChangeAvailable(false);
        return;
    }

    try {
        const response = await isVesselCapacityChanged(vesselMstId);

        const isChanged =
            typeof response === "boolean"
                ? response
                : response?.isVesselCapacityChanged === true ||
                  response?.IsVesselCapacityChanged === true;

        setIsCapacityChangeAvailable(isChanged);
    } catch (error) {
        console.error("Error checking vessel capacity change:", error);
        setIsCapacityChangeAvailable(false);
    }
};

checkVesselCapacityChange();

}, [reportDetails?.VesselMstId]);


	const handleForm = async () => {


		// Format the date with time
		const formattedDate = getFormattedLastVoyageDate();
		const confirmManualCIIUpdateObj = {
			attainedCIIWithoutExclusion: Number(
				ciiExclusion?.attainedCIIWithoutExclusion ?? 0
			).toFixed(3),
			attainedCIIWithExclusion: Number(
				ciiExclusion?.attainedCIIWithExclusion ?? 0
			).toFixed(3),
			attainedRequiredCII: Number(
				+ciiExclusion?.requiredCII ?? 0
			).toFixed(3),
			attainedCIIGradeWithoutExclusion: "",
			attainedCIIGradeWithExclusion: "",
			lastVoyageDate: formattedDate,
			isVesselsMajorConversion: ciiExclusion?.isVesselsMajorConversion,
		};

		try {
			const response = await confirmManualCIIUpdate(reportDetails.ReportMstId, confirmManualCIIUpdateObj);

			let manualCIIGrade = response;
			if (manualCIIGrade) {
				getInitialManualCiiData(reportDetails.ReportMstId);
				emitToasterMsg("success", "CII Data", "CII calculation completed.");
			}
		} catch (error) {
			emitToasterMsg("error", error.response.data.errorMessage);
		}
	};

	const GetReportFlagsByStatus = async (reportMasterId) => {
		await accessAPIForResponse("GetReportFlags", null, [reportMasterId]).then((response) => {
			setReportFlags(response);
		});
	};

	const handleClearCIIManualForm = async () => {
		// Manually set values to 0 (to ensure form state is updated correctly)
		setValue("ciiExclusion.requiredCII", 0);
		setValue("ciiExclusion.attainedCIIWithoutExclusion", 0);
		setValue("ciiExclusion.attainedCIIWithExclusion", 0);
		setValue("ciiExclusion.gradeWithoutExclusion", "");
		setValue("ciiExclusion.gradeWithExclusion", "");
		setValue("ciiExclusion.isVesselsMajorConversion", false);

		// Reset the form
		reset({
			ciiExclusion: {
				requiredCII: 0,
				attainedCIIWithoutExclusion: 0,
				attainedCIIWithExclusion: 0,
				gradeWithoutExclusion: "A",
				gradeWithExclusion: "A",
			},
		});

		// Persist the cleared state (send it to the API)
		const clearedFormObj = {
			attainedCIIWithoutExclusion: 0,
			attainedCIIWithExclusion: 0,
			requiredCII: 0,
			attainedCIIGradeWithoutExclusion: "X",
			attainedCIIGradeWithExclusion: "X",
		};

		try {
			// Make an API call to update the cleared values on the server
			await confirmManualCIIUpdate(reportDetails.ReportMstId, clearedFormObj);
		} catch (error) {
			console.error("Error clearing form on the server:", error);
		}

		// Optionally, persist the cleared state locally as well
		localStorage.setItem("ciiExclusionData", JSON.stringify(clearedFormObj));
	};

	const getInitialManualCiiData = async (reportMstId) => {
		try {
			const response = await fetchManualCII(reportMstId);
			if (response) {
				let manualCIIGrade = response.data;
				setValue("ciiExclusion.requiredCII", manualCIIGrade.requiredCII);
				setValue("ciiExclusion.attainedCIIWithoutExclusion", manualCIIGrade.ciiWithoutExclusion);
				setValue("ciiExclusion.attainedCIIWithExclusion", manualCIIGrade.ciiWithExclusion);
				setValue("ciiExclusion.gradeWithExclusion", manualCIIGrade.ciiGradeWithExclusion);
				setValue("ciiExclusion.gradeWithoutExclusion", manualCIIGrade.ciiGradeWithoutExclusion);
				setValue("ciiExclusion.isVesselsMajorConversion", manualCIIGrade.isVesselsMajorConversion?? false);
			}
		} catch (err) {
			console.error(err);
		}
	};

	console.log("ciiexclusion",getValues("ciiExclusion"));
	//future scope
	useEffect(() => {
		GetReportFlagsByStatus(reportDetails.ReportMstId);
		getInitialManualCiiData(reportDetails.ReportMstId);
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const savedData = localStorage.getItem("ciiExclusionData");
				if (!savedData || JSON.parse(savedData).requiredCII === "0") {
					const apiData = await getInitialManualCiiData(reportDetails.ReportMstId);
					//reset(apiData);
				} else {
					reset(
						JSON.parse(savedData) || {
							ciiExclusion: {
								requiredCII: "0",
								attainedCIIWithoutExclusion: "0",
								attainedCIIWithExclusion: "0",
								gradeWithoutExclusion: "A",
								gradeWithExclusion: "A",
							},
						}
					);
				}
			} catch (error) {
				console.error("Error fetching CII data", error);
			}
		};

		fetchData();
	}, [reportDetails.ReportMstId, reset]);

		const fetchCIIData = async () => {
		setLoader(true);
		try {
			const response = await getCIIdata(reportDetails.ReportMstId);
			let voyageDate = response?.data?.lastVoyageDate;
			setCIIData(response?.data || []);
			let dateInfo = voyageDate instanceof Date ? moment(voyageDate).format("DD MMM YYYY") : voyageDate;
			setLastVoyageDate(dateInfo);
		} catch (error) {
			console.error("Error fetching data:", error);
		} finally {
			setLoader(false);
		}
	};

	useEffect(() => {
		fetchCIIData();
	}, [ciiRefreshTrigger]);	


	// Refresh the YTD CII values after verified CII data is saved or removed
	const handleVerifiedCiiChange = (change) => {
		if (change?.saved || change?.removed) {
			fetchCIIData();
		}
	};

	return (
		<div>
			<CIIVerifiedInput setCIIData={setCIIData} onChange={handleVerifiedCiiChange} />
		<div className="ciidata-exclusions-container">
			<div className="d-flex align-items-center mb-1">
				<Controller
					name="ciiExclusion.isVesselsMajorConversion"
					control={control}
					defaultValue={false}
					render={({ field }) => (
						<CheckboxInput
							id="vessel-major-conversion"
							checked={field.value}
							disabled={!canEditMajorConversion|| !isCapacityChangeAvailable}
							onChange={async (checked) => {
								if (!canEditMajorConversion || !isCapacityChangeAvailable) {
									return;
								}

								field.onChange(checked);
								const values = getValues("ciiExclusion");
								const formattedDate = getFormattedLastVoyageDate();
								const payload = {
									attainedCIIWithoutExclusion: values.attainedCIIWithoutExclusion,
									attainedCIIWithExclusion: values.attainedCIIWithExclusion,
									attainedRequiredCII: +values.requiredCII,
									attainedCIIGradeWithoutExclusion: values.gradeWithoutExclusion,
									attainedCIIGradeWithExclusion: values.gradeWithExclusion,
									lastVoyageDate: formattedDate,
									isVesselsMajorConversion: checked,
								};

								try {
									await confirmManualCIIUpdate(reportDetails.ReportMstId, payload);
									setCiiRefreshTrigger((prev) => prev + 1);

									emitToasterMsg(
										"success",
										"CII Data",
										checked
											? "Major Conversion has been marked as non-substantial for the reporting year. CII information has been updated accordingly."
											: "Major Conversion is no longer marked as non-substantial for the reporting year. CII information has been updated accordingly."
									);
								} catch (error) {
									console.error(
										"Error updating Major Conversion:",
										error
									);

									emitToasterMsg(
										"error",
										"CII Data",
										error?.response?.data?.errorMessage || 
										error?.message || 
										"Failed to update Major Conversion."
									);
								}
							}}
						/>
					)}
				/>
				<span className="mb-0 ml-2 text-gray font-weight-bold vessel-Major-Conversion">{CII_DATA.CHECKBOX_LABEL}</span>
			</div>
			<Label className="mb-1 cii-exclusion-label cii-exclusion" bold name={CII_DATA.CII_EXCLUSIONS_HEADER} />
			<MDBRow className="col-12 pt-4 d-flex cii-exclusion">
				<MDBCol className="col-7 pl-0">
					<Label className="cii-exclusion-label" name={`${CII_DATA.REQUIRED_CII_TEXT}`} />
				</MDBCol>
				<MDBCol className="col-3 ">
					<Input
						id="attaines_required_cii"
						value={
							CIIData.attainedRequiredCII === -1
								? "Not Applicable"
								: CIIData.attainedRequiredCII != null && CIIData.attainedRequiredCII !== undefined
								? roundOff(CIIData.attainedRequiredCII, 3, 3)
								: 0.000
						}
						disabled
						type="text"
						readOnly
						className="readonly-input"
					/>
				</MDBCol>
			</MDBRow>
			<MDBRow className="col-12 pt-3 cii-exclusion">
				<MDBCol className="col-7  pl-0">
					<Label className="cii-exclusion-label" name={`${CII_DATA.ATTAINED_WITHOUT_EXCLUSIONS_TEXT} ${lastVoyageDate} `} />
				</MDBCol>
				<MDBCol className="col-3">
					<Input
						id="attained_without_exclusions"
						disabled
						name={`${CII_DATA.ATTAINED_WITHOUT_EXCLUSIONS_TEXT}`}
						value={
							CIIData.attainedCIIWithoutExclusion == -1
								? "Not Applicable"
								: CIIData.attainedCIIWithoutExclusion != null && CIIData.attainedCIIWithoutExclusion !== undefined
								? roundOff(CIIData.attainedCIIWithoutExclusion, 3, 3)
								: 0.000
						}
						type="text"
						readOnly
						className="readonly-input"
					/>
				</MDBCol>
			</MDBRow>
			<MDBRow className="col-12 pt-3 cii-exclusion">
				<MDBCol className="col-7  pl-0">
					<Label className="cii-exclusion-label" name={`${CII_DATA.ATTAINED_WITH_EXCLUSIONS_TEXT} ${lastVoyageDate}`} />
				</MDBCol>
				<MDBCol className="col-3">
					<Input
						id="attained_with_exclusions"
						value={
							CIIData.attainedCIIWithExclusion == -1
								? "Not Applicable"
								: CIIData.attainedCIIWithExclusion != null && CIIData.attainedCIIWithExclusion !== undefined
								? roundOff(CIIData.attainedCIIWithExclusion, 3, 3)
								: 0.000
						}
						disabled
						type="text"
						readOnly
						className="readonly-input"
					/>
				</MDBCol>
			</MDBRow>
			<MDBRow className="col-12 pt-3 cii-exclusion">
				<MDBCol className="col-7  pl-0">
					<Label className="cii-exclusion-label" name={`${CII_DATA.GRADES_WITHOUT_EXCLUSIONS_TEXT} ${lastVoyageDate}`} />
				</MDBCol>
				<MDBCol className="col-3">
					<Input
						id="attained_grade_without_exclusions"
						value={CIIData.attainedCIIGradeWithoutExclusion}
						disabled
						type="text"
						readOnly
						className="readonly-input"
					/>
				</MDBCol>
			</MDBRow>
			<MDBRow className="col-12 pt-3 cii-exclusion">
				<MDBCol className="col-7  pl-0">
					<Label className="cii-exclusion-label" name={`${CII_DATA.GRADES_WITH_EXCLUSIONS_TEXT} ${lastVoyageDate}`} />
				</MDBCol>
				<MDBCol className="col-3">
					<Input
						id="attained_grade_with_exclusions"
						value={CIIData.attainedCIIGradeWithExclusion}
						disabled
						type="text"
						readOnly
						className="readonly-input"
					/>
				</MDBCol>
			</MDBRow>
			{!isVesselEmissionOverviewCII && (
				<div>
					<Label bold name={CII_DATA.CLIENT_EXCLUSIONS_HEADER} className="mt-4 mb-3 cii-exclusion" />
					<form onSubmit={handleSubmit(handleForm)}>
						<MDBRow className="col-12 pt-3 cii-exclusion">
							<MDBCol className="col-7 p-0">
								<Label
									required
									className="cii-exclusion-label"
									name={`${CII_DATA.REQUIRED_CII_TEXT} ${lastVoyageDate}
									${CII_DATA.INPUT_CLIENT}`}
								/>
							</MDBCol>
							<MDBCol className="col-3">
								<Controller
    								name="ciiExclusion.requiredCII"
    								control={control}
    								rules={{
        								required: "CII of Vessel is required",
        								setValueAs: (value) =>
            								value === "" ? "" : Number(value).toFixed(3),
        								validate: (value) => {
            								// Initial 0 is invalid
            								if (!isRequiredCIIEdited && Number(value) === 0) {
                								return "CII of Vessel is required";
            						   		}

            				            // Empty is always invalid
            								if (value === "" || value === null || value === undefined) {
                								return "CII of Vessel is required";
            						   		}

            							// Once user has edited the field, 0 is allowed
            							return true;
        								},
    								}}
    								render={({ field }) => (
        							<div>
            							<Input
                							id="required_cii_input"
                							disabled={!reportFlags?.VoyagesEnabled}
                							onChange={(e) => {
                    						setIsRequiredCIIEdited(true);
                    						field.onChange(e);
                							}}
                							onKeyDown={(e) => {
                    						preventSpecialCharacters(e);

                    						if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                        					e.preventDefault();
                    						}
                					}}
                							type="number"
                							step="0.001"
                							value={field.value}
                							autoFocus={false}
                							className="cii-input"
                							onWheel={(e) => e.target.blur()}
            							/>
        							</div>
                                    )}
                               />
								<div className="mt-1">
									{errors?.ciiExclusion?.requiredCII && <div className="error">{errors?.ciiExclusion?.requiredCII?.message}</div>}
								</div>
							</MDBCol>
						</MDBRow>
						<MDBRow className="col-12 pt-3 cii-exclusion">
							<MDBCol className="col-7 p-0">
								<Label
									required
									className="cii-exclusion-label"
									name={`${CII_DATA.ATTAINED_WITHOUT_EXCLUSIONS_TEXT} ${lastVoyageDate}
									${CII_DATA.INPUT_CLIENT}`}
								/>
							</MDBCol>
							<MDBCol className="col-3">
								<Controller
									name="ciiExclusion.attainedCIIWithoutExclusion"
									control={control}
									rules={{
										required: "Estimated Attained CII without exclusion is required",
										setValueAs: (value) =>
											value === "" ? "" : Number(value).toFixed(3),
										validate: (value) => {
											// Initial 0 is invalid
											if (!isWithoutExclusionEdited && Number(value) === 0) {
												return "Estimated Attained CII without exclusion is required";
											}
											// Empty is always invalid
											if (value === "" || value === null || value === undefined) {
												return "Estimated Attained CII without exclusion is required";
											}
											// Once user has edited the field, 0 is allowed
											return true;
										},
									}}
									render={({ field }) => (
										<div>
											<Input
												required
												disabled={!reportFlags?.VoyagesEnabled}
												id="attained_cii_without_exclusions_input"
												onChange={(e) => {
													setIsWithoutExclusionEdited(true);
													field.onChange(e);
												}}
												onKeyDown={(e) => {
													preventScientificNotation(e);
													// Prevent up and down arrow keys
													if (e.key === "ArrowUp" || e.key === "ArrowDown") {
														e.preventDefault();
													}
												}}
												type="number"
												step="0.001"
												value={field.value}
												autoFocus={false}
												className="cii-input"
												onWheel={(e) => e.target.blur()}
											/>
										</div>
									)}
								/>
								<div className="mt-1">
									{errors?.ciiExclusion?.attainedCIIWithoutExclusion && (
										<div className="error">{errors?.ciiExclusion?.attainedCIIWithoutExclusion?.message}</div>
									)}
								</div>
							</MDBCol>
						</MDBRow>
						<MDBRow className="col-12 pt-3 cii-exclusion">
							<MDBCol className="col-7 p-0">
								<Label
									required
									className="cii-exclusion-label"
									name={`${CII_DATA.ATTAINED_WITH_EXCLUSIONS_TEXT} ${lastVoyageDate}
									${CII_DATA.INPUT_CLIENT}`}
								/>
							</MDBCol>
							<MDBCol className="col-3">
								<Controller
									name="ciiExclusion.attainedCIIWithExclusion"
									control={control}
									rules={{
										required: "Estimated Attained CII with exclusion is required",
										 setValueAs: (value) =>
										 	value === "" ? "" : Number(value).toFixed(3),
										validate: (value) => {
											if (!isWithExclusionEdited && Number(value) === 0) {
												return "Estimated Attained CII with exclusion is required";
											}

											if (value === "" || value === null || value === undefined) {
												return "Estimated Attained CII with exclusion is required";
											}

											return true;
										},
									}}
									render={({ field }) => (
										<div>
											<Input
												required
												disabled={!reportFlags?.VoyagesEnabled}
												id="attained_cii_with_exclusions_input"
												onChange={(e) => {
													setIsWithExclusionEdited(true);
													field.onChange(e);
												}}
												type="number"
												onKeyDown={(e) => {
													preventScientificNotation(e);

													if (e.key === "ArrowUp" || e.key === "ArrowDown") {
														e.preventDefault();
													}
												}}
												step="0.001"
												value={field.value}
												autoFocus={false}
												className="cii-input"
												onWheel={(e) => e.target.blur()}
											/>
										</div>
									)}
								/>
								<div className="mt-1">
									{errors?.ciiExclusion?.attainedCIIWithExclusion && (
										<div className="error">{errors?.ciiExclusion?.attainedCIIWithExclusion?.message}</div>
									)}
								</div>
							</MDBCol>
						</MDBRow>
						<MDBRow className="col-12 pt-3 cii-exclusion">
							<MDBCol className="col-7 p-0">
								<Label
									className="cii-exclusion-label"
									name={`${CII_DATA.GRADES_WITHOUT_EXCLUSIONS_TEXT} ${lastVoyageDate}
										${CII_DATA.CALCULATED_PORTAL_INFO}`}
									/>
								</MDBCol>
								<MDBCol className="col-3">
									<Controller
										name="ciiExclusion.gradeWithoutExclusion"
										control={control}
										render={({ field }) => (
											<div>
												<Input
													id="attained_cii_grade_without_exclusions_input"
													disabled={!reportFlags?.ConfirmCIIEnabled}
													onChange={(e) => {
														field.onChange(e.toUpperCase());
													}}
													readOnly
													type="text"
													defaultValue={field.value}
													value={field.value}
													autoFocus={false}
													className="cii-input"
												/>
											</div>
										)}
									/>
								</MDBCol>
							</MDBRow>
							<MDBRow className="col-12 pt-3 cii-exclusion">
								<MDBCol className="col-7 p-0">
									<Label
										className="cii-exclusion-label"
										name={`${CII_DATA.GRADES_WITH_EXCLUSIONS_TEXT} ${lastVoyageDate}
									${CII_DATA.CALCULATED_PORTAL_INFO}`}
									/>
								</MDBCol>
								<MDBCol className="col-3">
									<Controller
										name="ciiExclusion.gradeWithExclusion"
										control={control}
										render={({ field }) => (
											<div>
												<Input
													id="attained_cii_grade_with_exclusions_input"
													disabled={!reportFlags?.ConfirmCIIEnabled}
													onChange={(e) => {
														field.onChange(e.toUpperCase());
													}}
													type="text"
													readOnly
													defaultValue={field.value}
													value={field.value}
													autoFocus={false}
													className="cii-input"
												/>
											</div>
										)}
									/>
								</MDBCol>
							</MDBRow>
							<MDBRow className="col-12 pt-3 cii-exclusion">
								<MDBCol className="col-7 p-0">
									<Label bold name={CII_DATA.NOTE} className="cii-exclusion" />
								</MDBCol>
							</MDBRow>
							<div className="mt-5 d-flex justify-content-center">
								<button type="submit" className="confirm-btn-danger-lg" disabled={!reportFlags?.VoyagesEnabled}>
									{CII_DATA.CONFIRM_BTN_MNL_CII_UPDATE}
								</button>
								<Button
									type="button"
									variant="secondary"
									onClick={handleClearCIIManualForm}
									className="confirm-btn-primary-lg ml-3 "
									disabled={!reportFlags?.VoyagesEnabled}
								>
									{CII_DATA.REMOVE_BTN_MNL_CII_UPDATE}
								</Button>
							</div>
						</form>
					</div>
				)}
			</div>
		</div>
	);
};

export default CIIExclusionCalculation;
